import React, { FC, useEffect, useId, useRef, useState } from 'react';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import SVGInline from 'components/SVGInline/SVGInline';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import { useBackdropClose } from 'hooks/useBackdropClose/useBackdropClose';
import icons from 'style';
import { withNoScroll } from 'hooks/withNoScroll/withNoScroll';
import { MILLISECONDS, SECONDS } from 'utils/TimeUnit';
import translate from 'utils/fnTranslate';
import { ModalCloseButton, ModalWrapper } from 'style/styled-components/reusable.css';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { hexToRgba } from 'utils/fnColors';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { PinInputGroup } from './InputPinGroup';
import {
    HintText,
    LockedPinCounter,
    ModalContentWrapper,
    ModalDetails,
    ModalText,
    ModalTitle,
    ResetButton,
    ResetContainer,
    BackButton,
    ErrorLabel,
} from './PinModal.css';

type PropTypes = {
    loading: boolean;
    modalTitle?: string;
    modalText?: string;
    resetInput?: boolean;
    lockPinUntil?: number;
    onPinInserted: () => void;
    closeOverlay: () => void;
    onBack?: () => void;
    canGoBack?: boolean;
    closePinDialog?: (notifyCallback: boolean) => void;
    hasError?: boolean;
    errorMessage?: string;
};

const PinModalComponent: FC<PropTypes> = ({
    modalTitle,
    modalText,
    resetInput,
    lockPinUntil,
    closeOverlay,
    onPinInserted,
    loading,
    onBack,
    canGoBack,
    closePinDialog,
    hasError,
    errorMessage,
}) => {
    const config = useConfig()?.config;
    const backButtonRef = useRef<HTMLButtonElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const pinInputGroupRef = useRef<HTMLDivElement>(null);

    const { black_color } = config?.layout_config ?? {};
    const { reset } = config?.api_config?.routes?.Pin ?? {};

    const contentScrollbarRef = useRef(null);
    const lockCountDown = useRef<number>(null);

    const [remainedLockSeconds, setRemainedLockSeconds] = useState<number>(0);
    const backdropClose = useBackdropClose(closeOverlay);

    const titleId = useId();
    const descriptionId = useId();
    const lockStateMounted = useRef(false);

    useKeyboardShortcuts({
        ...(canGoBack && onBack
            ? {
                  backspace: () => {
                      backButtonRef.current?.click();
                  },
              }
            : {}),
    });

    useEffect(() => {
        const now = Date.now();

        if (lockPinUntil && lockPinUntil > now) {
            clearInterval(lockCountDown.current);

            setRemainedLockSeconds(Math.round(MILLISECONDS.toSeconds(lockPinUntil - Date.now())));
            lockCountDown.current = window.setInterval(() => {
                setRemainedLockSeconds(Math.round(MILLISECONDS.toSeconds(lockPinUntil - Date.now())));
            }, SECONDS.toMillis(1));
        } else {
            setRemainedLockSeconds(null);
            clearInterval(lockCountDown.current);
        }

        return () => clearInterval(lockCountDown.current);
    }, [lockPinUntil]);

    const now = Date.now();
    const isLocked = lockPinUntil && lockPinUntil > now;

    useEffect(() => {
        if (!lockStateMounted.current) {
            lockStateMounted.current = true;
            return;
        }

        if (isLocked) {
            (backButtonRef.current ?? closeButtonRef.current)?.focus();
        } else {
            pinInputGroupRef.current?.querySelector<HTMLElement>('[data-pin-input]')?.focus();
        }
    }, [isLocked]);

    const hintText = <HintText>{translate('RESET_PIN_HINT')}</HintText>;

    return (
        <ModalDialog
            as={ModalWrapper}
            onClose={closeOverlay}
            initialFocusRef={isLocked ? undefined : pinInputGroupRef}
            data-test-id={'modal-pin-screen'}
            aria-modal
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            tabIndex={-1}
            {...backdropClose}
        >
            {canGoBack && onBack && closePinDialog && (
                <BackButton
                    type="button"
                    ref={backButtonRef}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        closePinDialog(false);
                        onBack();
                    }}
                    tabIndex={0}
                    aria-label={translate('BACK_BUTTON')}
                >
                    <SVGInline svg={icons.icoGalleryLeft} />
                </BackButton>
            )}
            <ModalCloseButton
                type="button"
                ref={closeButtonRef}
                data-test-id={'header-settings-protectpin-close-button'}
                onClick={closeOverlay}
                tabIndex={0}
                aria-label={translate('CLOSE_BUTTON')}
            >
                <SVGInline svg={icons.closeIcon} />
            </ModalCloseButton>
            <ModalDetails ref={contentScrollbarRef}>
                <ModalTitle id={titleId} data-test-id={'header-settings-pin-title'}>
                    {modalTitle}
                </ModalTitle>
                <ModalContentWrapper id={descriptionId}>
                    <ModalText id="pin-change-cta" aria-live="polite" data-test-id={'header-settings-pin-text'}>
                        {modalText}
                    </ModalText>
                    {hasError && errorMessage && (
                        <ErrorLabel role="alert">
                            <SVGInline svg={icons.infoIcon} />
                            {errorMessage}
                        </ErrorLabel>
                    )}
                    {!isLocked && (
                        <PinInputGroup ref={pinInputGroupRef} onComplete={onPinInserted} reset={resetInput} ariaLabel={modalText} />
                    )}
                    {isLocked && (
                        <span aria-live="assertive" className="sr-only">
                            {modalText}
                        </span>
                    )}
                    {isLocked && <LockedPinCounter>{remainedLockSeconds}</LockedPinCounter>}
                    {isLocked && (
                        <ResetContainer>
                            <ResetButton label={translate('RESET_PIN_BUTTON')} isPrimary={true} link={reset} openNewTab={true} />
                            {hintText}
                        </ResetContainer>
                    )}
                </ModalContentWrapper>
            </ModalDetails>
            {isLocked && (
                <ResetContainer>
                    {hintText}
                    <ResetButton label={translate('RESET_PIN_BUTTON')} isPrimary={true} link={reset} openNewTab={true} />
                </ResetContainer>
            )}
            <LoadingIndicator overlay={true} isLoading={loading} backgroundOverwrite={hexToRgba(black_color, 0.5)} />
        </ModalDialog>
    );
};

export const PinModal = withNoScroll(PinModalComponent);
